import { signal } from '@angular/core';
import { Component, forwardRef } from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';

/**
 * Campo de data (`<input type="date">`) integrável a formulários
 * (`ControlValueAccessor`).
 *
 * Exibe a data no formato `YYYY-MM-DD` e propaga um objeto `Date` ao controle.
 */
@Component({
    selector: 'ai-date-input',
    imports: [FormsModule, ReactiveFormsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateInput),
            multi: true,
        },
    ],
    templateUrl: './date-input.component.html',
    styleUrl: './date-input.component.scss',
})
export class DateInput implements ControlValueAccessor {
    /** Valor exibido no input (formato `YYYY-MM-DD`). */
    value = signal<string | null>(null);

    /** Indica se o campo está desabilitado. */
    disabled = signal<boolean>(false);

    /** Callback de mudança do ControlValueAccessor. */
    onChange: (value: Date | null) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(value: Date | string | null): void {
        if (value) {
            const date = new Date(value);
            this.value.set(this.formatDate(date));
        } else {
            this.value.set(null);
        }
    }

    registerOnChange(fn: (value: Date | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
    }

    handleChange(event: Event) {
        const input = event.target as HTMLInputElement;
        const value = input.value;
        this.value.set(value);
        if (value) {
            const [year, month, day] = value.split('-').map(Number);
            this.onChange(new Date(year, month - 1, day));
        } else {
            this.onChange(null);
        }
    }

    handleBlur() {
        this.onTouched();
    }

    protected formatDate(date: Date): string {
        const offset = date.getTimezoneOffset();
        const local = new Date(date.getTime() - offset * 60000);
        return local.toISOString().slice(0, 10);
    }
}
